import { useContext, useEffect, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
  CREATING_SESSION,
  TenancyProvider,
} from "@bfc-software/app-toolbox";

export type WarehouseGateProps = {
  /** URL params of the warehouse layout; the gate lives in `-components/` so it cannot call `Route.useParams()`. */
  lang: string;
  org: string;
  location: string;
  /** Where an unknown warehouse goes: the product's front door. */
  fallbackTo: "/$lang/orgs/$org/internal" | "/$lang/orgs/$org/outbound";
  /** Rendered in place of `children` until session and URL agree. Each product keeps its own markup. */
  loading: ReactNode;
  children: ReactNode;
};

export function WarehouseGate({
  lang,
  org,
  location,
  fallbackTo,
  loading,
  children,
}: WarehouseGateProps): ReactNode {
  const { state, selectLocation } = useContext(AuthContext);
  const navigate = useNavigate();

  /**
   * Inside a product the URL owns the warehouse and the session follows it,
   * which is what makes a bookmark work. Reconciliation happens here and nowhere
   * else: the header selector only navigates, because two effects reconciling in
   * opposite directions loop.
   *
   * A warehouse the organisation does not have goes to the product's front
   * door. Re-minting against it would 401 on every pass, and rendering it shows
   * an empty product that reads as an empty warehouse.
   *
   * CREATING_SESSION counts as out of sync. The organisation's warehouses are
   * known in that state, but the AAA token is still the previous warehouse's -
   * the org layout keeps this subtree mounted while the session is re-minted, so
   * without this the screen below would fetch against the warehouse being left.
   */
  const authenticated = state.status === AUTHENTICATED;
  const remintingSession = state.status === CREATING_SESSION;
  const orgKnown = authenticated || remintingSession;

  const sessionWarehouse = authenticated ? state.locationId : null;
  const orgWarehouses = orgKnown
    ? AuthUtils.getLocations(state, state.orgId)
    : [];
  const urlWarehouseIsKnown = orgWarehouses.some(
    (warehouse) => warehouse.warehouseId === location,
  );
  const warehouseIsUnknown = orgKnown && !urlWarehouseIsKnown;
  const needsWarehouseSync =
    urlWarehouseIsKnown && sessionWarehouse !== location;
  // eslint-disable-next-line no-restricted-properties -- necessary: the gate is where org/warehouse ids are first derived from the URL slug; it asserts them for everything below via <TenancyProvider>, so it cannot read them from the tenancy context
  const orgId = orgKnown ? AuthUtils.getOrgIdByStub(state, org) : null;
  const warehouseReady =
    orgId !== null && urlWarehouseIsKnown && !needsWarehouseSync;

  useEffect(() => {
    if (!authenticated) return;
    if (!needsWarehouseSync) return;

    selectLocation(location);
  }, [authenticated, needsWarehouseSync, location, selectLocation]);

  useEffect(() => {
    if (!warehouseIsUnknown) return;

    navigate({
      to: fallbackTo,
      params: { lang, org },
      replace: true,
    });
  }, [warehouseIsUnknown, navigate, fallbackTo, lang, org]);

  /**
   * Held back until the session matches the warehouse in the URL, so a screen
   * never loads against the one being left - or against a warehouse the
   * organisation does not have, which is on its way to the front door. The
   * tenancy provider is rendered only once org and warehouse agree, so
   * everything below can read them as asserted.
   */
  return warehouseReady ? (
    <TenancyProvider orgId={orgId} warehouseId={location}>
      {children}
    </TenancyProvider>
  ) : (
    loading
  );
}
